import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { findKeyInObj } from "../../utils/object";
import deepmerge from "deepmerge";

const BrandContext = React.createContext<any>(null);

export interface GenericBrandProviderProps {
  brand: any;
  children: any;
}

export const GenericBrandProvider = ({
  children,
  brand,
}: GenericBrandProviderProps) => {
  const [override, setOverride] = useState<Record<string, any>>({});

  const memoizedContext = useMemo(() => {
    if (!brand) {
      return null;
    }

    return {
      brandData: deepmerge(brand, override),
      setOverride,
      removeOverride: () => setOverride({}),
      hasOverride: Object.keys(override).length > 0,
    };
  }, [brand, override]);

  if (!memoizedContext) {
    return null;
  }

  return (
    <BrandContext.Provider value={memoizedContext}>
      {children}
    </BrandContext.Provider>
  );
};

type GetProp = <T = unknown>(key: string) => T;

export function useBrand() {
  const ctx = useContext(BrandContext);

  const getProp = useCallback<GetProp>(
    (key) => {
      // ? needed for tests that access brand but have not set it up because
      // they don't care about it
      return findKeyInObj(ctx?.brandData, key);
    },
    [ctx]
  );

  return useMemo(
    () => ({
      getProp,
    }),
    [getProp]
  );
}

export function withBrand(Component) {
  return function ComponentWithBrand(props) {
    const brand = useBrand();
    return <Component {...props} brand={brand} />;
  };
}

/**
 * Only one of these can be active at a time
 * @param obj Watch out for objects that change on render but don't have to!
 */
export function useBrandOverride(obj) {
  const ctx = useContext(BrandContext);

  useEffect(() => {
    ctx.setOverride(obj);
    return () => {
      ctx.removeOverride();
    };
  }, [ctx, obj]);

  return ctx.hasOverride;
}
